import { axiosInstance } from "../../utils/axios";
import { AxiosResponse } from "axios";
import { ElaborationResponse } from "../models";
import { CoObjectType, DimensionDetails } from "../../components/interfaces/CoObject";
import { FormatMetadata } from "../models/format-metadata";
import { TgkTableParameter } from "../models/tgk-table-parameter";
import { FormatImage } from "../models/format-image";
import { TableUpdateStrategy } from "../../components/coobjects/co-panel/ObjectPanelComponent";
import { AnalyticalWorkspace } from "../models/analytical-workspace";
import { Dataset } from "../models/dataset";
import { DatasetField } from "../models/dataset-field";
import { Process } from "../models/process";

export type ElaborationItemType = "NEW" | "EDIT" | "REFRESH";
export type ElaborationDepthLevelType = "DEEPEST" | "SHALLOWEST";
export type ObjectUpdateOptionType = "DATAFULL" | "DATALESS";

export interface ElaborationItem {
  correlationId: string;
  contentControlId: string;
  dbId: string;
  formId?: string;
  formDesc?: string;
  sheetCode?: string;
  sheet?: string;
  area?: string;
  cell?: string;
  chart?: string;
  analyticalWorkspace?: AnalyticalWorkspace;
  dataset?: Dataset;
  amount?: DatasetField;
  richText?: DatasetField;
  process?: Process;
  dimensions?: DimensionDetails[];
  objectId: string;
  objectType: CoObjectType;
  documentPartIncludedId?: string;
  type: ElaborationItemType;
  formatMetadata?: FormatMetadata;
  formatImage?: FormatImage;
  inheritFormat?: boolean;
  inheritFormatImage?: boolean;
  formatType?: string;
  parameters?: TgkTableParameter[];
  updateOptions?: UpdateOptions;
  elabResultType?: ElabResultType;
  source?: string;
  tableUpdateStrategy?: TableUpdateStrategy;
  componentLogicId?: string;
}

export interface UpdateOptions {
  depthLevel?: ElaborationDepthLevelType;
  objectUpdateOption?: ObjectUpdateOptionType;
}

export interface ElaborationRequest {
  correlationId: string;
  clientId: string;
  documentId: string;
  userId: string;
  tgkElaborationItemList: ElaborationItem[];
  locale?: string;
}

export interface CallbackElaborationStatusBase {
  correlationId: string;
  clientId: string;
  documentId: string;
  userId: string;
}

export interface CallbackElaborationStatus extends CallbackElaborationStatusBase {
  items: CallbackElaborationStatusItem[];
}

export interface CallbackElaborationsSnapshotStatus {
  snapshot: CallbackElaborationStatus[];
}

export interface CallbackSingleElaborationItemStatus extends CallbackElaborationStatusBase {
  singleItem: CallbackElaborationStatusItem;
}

export interface CallbackHeartbeatStatus {
  up: string;
}

export interface CallbackElaborationStatusItem {
  id: string;
  contentControlId: string;
  status:
    | "REQUESTED"
    | "STARTED"
    | "REJECTED"
    | "REJECTED_FOR_CONCURRENCY"
    | "FAILED"
    | "COMPLETED"
    | "REJECTED_FOR_OBJECT_NOT_FOUND"
    | "ABORTED";
  startDate: string;
  endDate: string;
  elabResult: string;
  elabResultType: ElabResultType;
  correlationId: string;
  formSheetAreaId: CallbackElaborationFormSheetArea;
  resultAdded: boolean;
  userChecked: boolean;
  messages: CallbackElaborationMessage[];
  objectName: string;
}

export type ElabResultType = "HTML" | "STRINGBASE64" | "IMAGE_STRINGBASE64" | "REF_URL" | "TEXT";

export interface CallbackElaborationFormSheetArea {
  dbId: string;
  formId: string;
  sheet: string;
  area: string;
}

export interface CallbackElaborationMessage {
  text: string;
  details: string;
  severity: string;
  traceId: string;
}

export function createElaborationRequest(elaboration: ElaborationRequest): Promise<ElaborationResponse> {
  return axiosInstance.post(`/elaboration/create`, elaboration).then((resp) => resp.data);
}

export function confirmElaborationItemResultAdd(itemId: string): Promise<AxiosResponse> {
  return axiosInstance.post(`/elaboration/item/${itemId}/confirmElaborationResultAdd`);
}

export function confirmElaborationItemUserCheck(itemId: string): Promise<AxiosResponse> {
  return axiosInstance.post(`/elaboration/item/${itemId}/confirmElaborationUserCheck`);
}

export function confirmElaborationItemsResultAddAndUserChecked(
  correlationId: string,
  addedItemIds: string[],
  userCheckedItemIds: string[]
): Promise<AxiosResponse[]> {
  return axiosInstance.post(`/elaboration/items/confirm`, {
    correlationId: correlationId,
    addedItemIds: addedItemIds,
    userCheckedItemIds: userCheckedItemIds,
  });
}

export function notifyFailedElaboration(itemId: string, error: string): Promise<AxiosResponse> {
  return axiosInstance.post(`/elaboration/item/${itemId}/notifyFailedElaboration`, error);
}

export function confirmElaborationItemsResultAddSilent(itemId: string): Promise<AxiosResponse[]> {
  return axiosInstance.post(`/elaboration/item/${itemId}/notNotifiedConfirmElaborationResultAdd`);
}
